import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getStockDetail } from '../../services/inventoryApi';
import { PageHeader, Card, Badge, Button } from '../ui';

const fmtQty = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
};
const fmtTime = (t) => (t ? new Date(t).toLocaleString() : '—');

const SOURCE_LABELS = {
  full: 'Full sync',
  incremental: 'Changes sync',
  targeted: 'Single-SKU sync',
};

// Sub-bucket rows for the per-warehouse breakdown table.
const SUB_BUCKETS = [
  ['Sales', 'sales_demand_qty', 'sales_reserved_qty', 'sales_allocated_qty', 'sales_picking_qty', 'sales_picked_qty'],
  ['Jobs', 'job_demand_qty', 'job_reserved_qty', 'job_allocated_qty', 'job_picking_qty', 'job_picked_qty'],
  ['Transfers', 'tford_demand_qty', 'tford_reserved_qty', 'tford_allocated_qty', 'tford_picking_qty', 'tford_picked_qty'],
];

const StockDetail = () => {
  const { company, partNum } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await getStockDetail(company, partNum);
      setData(res);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      setData(null);
    }
  }, [company, partNum]);

  useEffect(() => {
    load();
  }, [load]);

  const levels = data?.levels || [];
  const history = data?.history || [];

  return (
    <div className="sync-page stock-detail">
      <PageHeader
        title={`${partNum} — ${company}`}
        sub="All warehouses for this SKU with the full Epicor bucket breakdown, plus the recorded change history (a row is written whenever a bucket moved). Available to sell = on hand − (non-sales commitments + max(sales commitments, open unshipped order demand)), floored at zero."
        actions={<Button variant="secondary" to="/inventory/stock">Back to stock levels</Button>}
      />

      {error && <div className="banner error">{error}</div>}

      <section className="card">
        <h3 className="section-h">Warehouse levels</h3>
        <table>
          <thead>
            <tr>
              <th>Warehouse</th>
              <th style={{ textAlign: 'right' }}>Available to sell</th>
              <th style={{ textAlign: 'right' }}>Available (raw)</th>
              <th style={{ textAlign: 'right' }}>On hand</th>
              <th style={{ textAlign: 'right' }}>Reserved</th>
              <th style={{ textAlign: 'right' }}>Allocated</th>
              <th style={{ textAlign: 'right' }}>Picking</th>
              <th style={{ textAlign: 'right' }}>Picked</th>
              <th style={{ textAlign: 'right' }} title="Open unshipped sales order demand (held subset shown in parentheses)">Open demand</th>
              <th style={{ textAlign: 'right' }}>Demand</th>
              <th style={{ textAlign: 'right' }}>Min</th>
              <th style={{ textAlign: 'right' }}>Safety</th>
              <th>Status</th>
              <th>Synced</th>
            </tr>
          </thead>
          <tbody>
            {!levels.length && (
              <tr><td colSpan={14} className="muted">No inventory recorded for this SKU.</td></tr>
            )}
            {levels.map((l) => (
              <tr key={`${l.plant}-${l.warehouse_code}`} style={l.warehouse_enabled === false ? { opacity: 0.55 } : undefined}>
                <td>
                  <strong>{l.warehouse_code}</strong>
                  {l.plant ? <span className="muted"> ({l.plant})</span> : null}
                  {l.warehouse_enabled === false && (
                    <>
                      {' '}
                      <Badge variant="neutral">warehouse disabled</Badge>
                    </>
                  )}
                </td>
                <td style={{ textAlign: 'right' }}><strong>{fmtQty(l.available_to_sell)}</strong></td>
                <td style={{ textAlign: 'right', color: Number(l.available_raw) < 0 ? '#c0392b' : undefined }}>
                  {fmtQty(l.available_raw)}
                </td>
                <td style={{ textAlign: 'right' }}>{fmtQty(l.on_hand_qty)}</td>
                <td style={{ textAlign: 'right' }}>{fmtQty(l.reserved_qty)}</td>
                <td style={{ textAlign: 'right' }}>{fmtQty(l.allocated_qty)}</td>
                <td style={{ textAlign: 'right' }}>{fmtQty(l.picking_qty)}</td>
                <td style={{ textAlign: 'right' }}>{fmtQty(l.picked_qty)}</td>
                <td style={{ textAlign: 'right' }}>
                  {fmtQty(l.open_demand_qty)}
                  {Number(l.open_demand_held_qty) > 0 && (
                    <span className="muted" title="Held-order subset of open demand">
                      {' '}({fmtQty(l.open_demand_held_qty)} held)
                    </span>
                  )}
                </td>
                <td style={{ textAlign: 'right' }}>{fmtQty(l.demand_qty)}</td>
                <td style={{ textAlign: 'right' }}>{fmtQty(l.minimum_qty)}</td>
                <td style={{ textAlign: 'right' }}>{fmtQty(l.safety_qty)}</td>
                <td>
                  {l.is_present
                    ? Number(l.available_to_sell) > 0
                      ? <Badge variant="success">in stock</Badge>
                      : <Badge variant="danger">out of stock</Badge>
                    : <Badge variant="neutral">absent</Badge>}
                </td>
                <td className="muted">{fmtTime(l.synced_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {levels.map((l) => (
        <Card
          key={`sub-${l.plant}-${l.warehouse_code}`}
          title={`Bucket breakdown — ${l.warehouse_code}`}
          desc="Per-source demand/reservation split (sales orders, jobs, transfer orders)."
        >
          <table>
            <thead>
              <tr>
                <th>Source</th>
                <th style={{ textAlign: 'right' }}>Demand</th>
                <th style={{ textAlign: 'right' }}>Reserved</th>
                <th style={{ textAlign: 'right' }}>Allocated</th>
                <th style={{ textAlign: 'right' }}>Picking</th>
                <th style={{ textAlign: 'right' }}>Picked</th>
              </tr>
            </thead>
            <tbody>
              {SUB_BUCKETS.map(([label, ...cols]) => (
                <tr key={label}>
                  <td>{label}</td>
                  {cols.map((c) => (
                    <td key={c} style={{ textAlign: 'right' }}>{fmtQty(l[c])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ))}

      <section className="card">
        <h3 className="section-h">Change history</h3>
        <p className="muted">
          Most recent stock movements first. A history row is only written when a bucket actually
          changed, so gaps mean nothing moved. History is retained for 90 days.
        </p>
        <table>
          <thead>
            <tr>
              <th>Recorded</th>
              <th>Warehouse</th>
              <th style={{ textAlign: 'right' }}>Available to sell</th>
              <th style={{ textAlign: 'right' }}>On hand</th>
              <th style={{ textAlign: 'right' }}>Reserved</th>
              <th style={{ textAlign: 'right' }}>Allocated</th>
              <th style={{ textAlign: 'right' }}>Picking</th>
              <th style={{ textAlign: 'right' }}>Picked</th>
              <th style={{ textAlign: 'right' }}>Open demand</th>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            {!history.length && (
              <tr><td colSpan={10} className="muted">No changes recorded yet.</td></tr>
            )}
            {history.map((h, i) => (
              <tr key={i}>
                <td className="muted">{fmtTime(h.recorded_at)}</td>
                <td>{h.warehouse_code}</td>
                <td style={{ textAlign: 'right' }}>
                  <strong>{fmtQty(h.available_to_sell)}</strong>
                  {!h.is_present && <span className="muted"> (removed)</span>}
                </td>
                <td style={{ textAlign: 'right' }}>{fmtQty(h.on_hand_qty)}</td>
                <td style={{ textAlign: 'right' }}>{fmtQty(h.reserved_qty)}</td>
                <td style={{ textAlign: 'right' }}>{fmtQty(h.allocated_qty)}</td>
                <td style={{ textAlign: 'right' }}>{fmtQty(h.picking_qty)}</td>
                <td style={{ textAlign: 'right' }}>{fmtQty(h.picked_qty)}</td>
                <td style={{ textAlign: 'right' }}>
                  {fmtQty(h.open_demand_qty)}
                  {Number(h.open_demand_held_qty) > 0 && (
                    <span className="muted"> ({fmtQty(h.open_demand_held_qty)} held)</span>
                  )}
                </td>
                <td className="muted">{SOURCE_LABELS[h.change_source] || h.change_source}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
};

export default StockDetail;
